import React from 'react'
import _ from 'lodash'
import AnimateHeight from 'react-animate-height'
import PropTypes from 'prop-types'
import { css, StyleSheet } from 'aphrodite/no-important'

import { colors } from '@lmc/common/constants'
import {
  RoundColumnCell,
  StatusColumnCell,
  DetailsColumnCell,
} from './columnCells'

const renderRow = (props) => {
  const { isOpen, setIsOpen } = props

  return (
    <div
      className={css(classes.row)}
      onClick={() => {
        setIsOpen(!isOpen)
      }}
      style={styles.row}
    >
      <RoundColumnCell {...props} />
      <StatusColumnCell {...props} />
      <DetailsColumnCell {...props} />
    </div>
  )
}

// Main render function

/**
@description Renders a rounds report table row.

@param {string} time. The round time.

@param {number} due. The residents whose meds are due (i.e. not signed).
If a round is the most recent round, the residents will be counted as due.
If a round is not the most recent round, the residents will be counted as
not signed.

@param {number} notSigned. The residents whose meds have not been signed.
These will be meds that are due, but are in rounds in the past, excluding
the most recent round.

@param {number} signed. The residents whose meds have been signed for
(either given or not given).

@param {number} notAdministered. The residents who haven't taken all
their meds.

@param {number} PRN. The residents who have had prn meds.

@param {bool} isFinalRow. True if row is last row in table.

@returns {function} RoundsReportTableRow
*/

export default function RoundsReportTableRow(props) {
  const {
    children,
    due,
    isDailyTotal,
    isFinalRow,
    notAdministered,
    notSigned,
    PRN,
    signed,
  } = props

  const [isOpen, setIsOpen] = React.useState(false)

  const residentsTotal = _.sum([due, notAdministered, notSigned, PRN, signed])
  const isEmptyRow = !residentsTotal

  const rowContainerStyles = {
    ...(isDailyTotal
      ? styles.dailyTotalRow
      : isFinalRow
      ? styles.finalRowContainer
      : styles.rowContainer),
    ...(isEmptyRow && styles.dailyTotalRow),
  }

  return (
    <div style={rowContainerStyles}>
      {renderRow({ ...props, isEmptyRow, isOpen, setIsOpen })}
      <AnimateHeight animateOpacity duration={200} height={isOpen ? 'auto' : 0}>
        {children}
      </AnimateHeight>
    </div>
  )
}

RoundsReportTableRow.propTypes = {
  PRN: PropTypes.number.isRequired,
  due: PropTypes.number.isRequired,
  isDailyTotal: PropTypes.bool,
  isFinalRow: PropTypes.bool,
  notAdministered: PropTypes.number.isRequired,
  notSigned: PropTypes.number.isRequired,
  signed: PropTypes.number.isRequired,
  time: PropTypes.string.isRequired,
}

const classes = StyleSheet.create({
  row: {
    ':hover': {
      background: colors.lightgrey,
    },
  },
})

const styles = {
  dailyTotalRow: {
    pointerEvents: 'none',
  },
  finalRowContainer: {
    borderBottom: `2px solid ${colors.darkgrey}`,
  },
  row: {
    alignItems: 'center',
    cursor: 'pointer',
    display: 'flex',
    height: 72,
    justifyContent: 'space-between',
    lineHeight: '22px',
    padding: '0px 16px',
    transition: 'all .2s ease',
  },
  rowContainer: {
    borderBottom: `1px solid ${colors.grey}`,
  },
}
